import { useState, useEffect } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Crown, Home } from "lucide-react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminMobileDropdown from "../../components/AdminMobileDropdown";
import AdminContent from "../../components/AdminContent";

export default function AdminDashboard() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    newUsersThisMonth: 0,
    totalCourses: 0,
    activeCourses: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    growthRate: 0,
    revenueThisMonth: 0,
    revenueLastMonth: 0,
  });
  const [users, setUsers] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState({
    pendingTrials: 0,
    pendingPayments: 0
  });

  useEffect(() => {
    // Admin access is handled by AdminPasswordGuard, no need to check Firebase auth
    loadAdminData();
  }, [navigate]);

  const loadAdminData = async () => {
    try {
      // Load all users
      const usersSnap = await getDocs(collection(db, "users"));
      const usersData = usersSnap.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));
      setUsers(usersData);

      // Load all courses
      const coursesSnap = await getDocs(collection(db, "courses"));
      const activeCourses = coursesSnap.docs.filter(doc => doc.data().published).length;

      // Load all payments (course purchases) - only count instructor-approved payments
      const paymentsSnap = await getDocs(collection(db, "payments"));
      const courseRevenue = paymentsSnap.docs
        .filter(doc => doc.data().status === 'completed' && doc.data().approvedBy)
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Load all subscription payments (instructor subscriptions) - only count admin-approved payments
      const subscriptionPaymentsSnap = await getDocs(collection(db, "subscription_payments"));
      const subscriptionRevenue = subscriptionPaymentsSnap.docs
        .filter(doc => doc.data().status === 'approved')
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Calculate total revenue
      const totalRevenue = courseRevenue + subscriptionRevenue;

      // Calculate revenue this month and last month
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      const revenueThisMonth = [
        ...paymentsSnap.docs.filter(doc => doc.data().status === 'completed' && doc.data().approvedBy),
        ...subscriptionPaymentsSnap.docs.filter(doc => doc.data().status === 'approved')
      ]
        .filter(doc => {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate();
          return createdAt && createdAt >= thisMonthStart;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      const revenueLastMonth = [
        ...paymentsSnap.docs.filter(doc => doc.data().status === 'completed' && doc.data().approvedBy),
        ...subscriptionPaymentsSnap.docs.filter(doc => doc.data().status === 'approved')
      ]
        .filter(doc => {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate();
          return createdAt && createdAt >= lastMonthStart && createdAt <= lastMonthEnd;
        })
        .reduce((sum, doc) => sum + (Number(doc.data().amount) || 0), 0);

      // Calculate growth rate
      const growthRate = revenueLastMonth > 0 
        ? ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100 
        : 0;

      // Count active subscriptions
      const activeSubscriptions = usersData.filter(u => 
        u.subscriptionTier && 
        u.subscriptionTier !== 'free' && 
        u.subscriptionTier !== 'Free'
      ).length;

      // Count new users this month
      const newUsersThisMonth = usersData.filter(u => {
        const createdAt = u.createdAt?.toDate();
        return createdAt && createdAt >= thisMonthStart;
      }).length;
      
      setStats({
        totalUsers: usersData.length,
        newUsersThisMonth,
        totalCourses: coursesSnap.size,
        activeCourses,
        activeSubscriptions,
        totalRevenue,
        growthRate,
        revenueThisMonth,
        revenueLastMonth,
      });

      // Load pending approvals from Salepage System
      const pendingTrialsQuery = query(
        collection(db, "users"),
        where("salePageSubscription.status", "==", "trial_pending")
      );
      const pendingTrialsSnap = await getDocs(pendingTrialsQuery);
      
      const pendingPaymentsQuery = query(
        collection(db, "salePagePayments"),
        where("status", "==", "pending")
      );
      const pendingPaymentsSnap = await getDocs(pendingPaymentsQuery);

      setPendingApprovals({
        pendingTrials: pendingTrialsSnap.size,
        pendingPayments: pendingPaymentsSnap.size
      });

    } catch (error) {
      console.error("Error loading admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    
    // Reload data when switching to subscription management to ensure fresh data
    if (section === 'subscription-management') {
      console.log('[AdminDashboard] Reloading data for subscription management...');
      loadAdminData();
    }
  };

  const sectionTitleMap: Record<string, string> = {
    overview: 'ภาพรวม',
    users: 'ผู้ใช้',
    'all-users': 'ผู้ใช้ทั้งหมด',
    instructors: 'ผู้สอน',
    students: 'นักเรียน',
    courses: 'คอร์ส',
    'all-courses': 'คอร์สทั้งหมด',
    'flagged-courses': 'คอร์สที่ถูกรายงาน',
    revenue: 'รายได้',
    'total-revenue': 'รายได้รวม',
    'subscription-payments': 'การชำระเงิน',
    'payment-approval': 'อนุมัติการชำระเงิน',
    management: 'การจัดการ',
    'subscription-management': 'จัดการ Subscription',
    'package-management': 'จัดการแพ็กเกจ',
    'discount-management': 'จัดการส่วนลด',
    'storage-management': 'จัดการพื้นที่',
    'system-settings': 'ตั้งค่าระบบ',
    monitoring: 'การตรวจสอบ',
    'activity-logs': 'บันทึกกิจกรรม',
    'system-health': 'สถานะระบบ',
    notifications: 'การแจ้งเตือน',
    reports: 'รายงาน',
    analytics: 'วิเคราะห์ข้อมูล',
    'usage-stats': 'สถิติการใช้งาน',
    'ai-system': 'ระบบ AI',
  };

  const sectionTitle = sectionTitleMap[activeSection] ?? 'ภาพรวม';

  useEffect(() => {
    const pathMap: Record<string, string> = {
      '/admin': 'overview',
      '/admin/dashboard': 'overview',
      '/admin/ai-system': 'ai-system',
    };

    const mapped = pathMap[location.pathname];
    if (mapped) {
      setActiveSection(mapped);
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        setActiveSection(hash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-purple-50">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-white/30 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-500/20">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Admin</h1>
                    <span className="hidden sm:inline text-sm text-gray-500 truncate">/ {sectionTitle}</span>
                  </div>
                  <p className="text-gray-500 text-sm truncate">ระบบจัดการหลังบ้าน EduMi</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <Link to="/">
                <button className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl font-medium text-gray-700 shadow-sm transition-all">
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">หน้าแรก</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Layout */}
      <div className="max-w-7xl mx-auto py-8 px-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block">
            <AdminSidebar
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </div>

          {/* Mobile Dropdown */}
          <div className="lg:hidden w-full">
            <AdminMobileDropdown
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <div className="mb-4 hidden lg:flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{sectionTitle}</h2>
                <p className="text-sm text-gray-500">อัปเดตล่าสุดจากข้อมูลในระบบ</p>
              </div>
            </div>
            <AdminContent
              activeSection={activeSection}
              stats={stats}
              users={users}
              topStorageUsers={[]}
              recentLogs={[]}
              onSectionChange={handleSectionChange}
              pendingApprovals={pendingApprovals}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
